import React from 'react';
import { Phone, MapPin, CreditCard, ArrowLeft, Check, Loader2 } from 'lucide-react';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import PhoneInput from 'react-phone-number-input';

const AddressForm = ({
  formData,
  loading,
  handleChange,
  handleCountryChange,
  setFormData,
  setStep,
  onSubmit,
  translations
}) => {
  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="country" className="block text-sm font-medium text-gray-700">
          {translations("profile.country")} <span className="text-red-500">*</span>
        </label>
        <Select
          options={countryList().getData()}
          value={countryList().getData().find(option => option.value === formData.country)}
          onChange={handleCountryChange}
          className="mt-1"
          placeholder={translations("profile.selectCountry")}
        />
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-indigo-600" />
          {translations("profile.contactInfo")}
        </h3>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            {translations("profile.phone")} <span className="text-red-500">*</span>
          </label>
          <PhoneInput
            international
            value={formData.phone}
            onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-indigo-600" />
          {translations("profile.addressInfo")}
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                {translations("profile.street")} <span className="text-red-500">*</span>
              </label>
              <input
                id="street"
                name="street"
                type="text"
                value={formData.street}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="doorNumber" className="block text-sm font-medium text-gray-700">
                  {translations("profile.doorNumber")} <span className="text-red-500">*</span>
                </label>
                <input
                  id="doorNumber"
                  name="doorNumber"
                  type="text"
                  value={formData.doorNumber}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="apartment" className="block text-sm font-medium text-gray-700">
                  {translations("profile.apartment")} <span className="text-red-500">*</span>
                </label>
                <input
                  id="apartment"
                  name="apartment"
                  type="text"
                  value={formData.apartment}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="district" className="block text-sm font-medium text-gray-700">
                {translations("profile.district")} <span className="text-red-500">*</span>
              </label>
              <input
                id="district"
                name="district"
                type="text"
                value={formData.district}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                {translations("profile.city")} <span className="text-red-500">*</span>
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="parish" className="block text-sm font-medium text-gray-700">
                {translations("profile.parish")} <span className="text-red-500">*</span>
              </label>
              <input
                id="parish"
                name="parish"
                type="text"
                value={formData.parish}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                {translations("profile.postalCode")} <span className="text-red-500">*</span>
              </label>
              <input
                id="postalCode"
                name="postalCode"
                type="text"
                value={formData.postalCode}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-indigo-600" />
          {translations("profile.additionalInfo")}
        </h3>
        <div>
          <label htmlFor="nif" className="block text-sm font-medium text-gray-700">
            {translations("profile.nif")} <span className="text-red-500">*</span>
          </label>
          <input
            id="nif"
            name="nif"
            type="text"
            value={formData.nif}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <ArrowLeft className="w-4 h-4" />
          {translations("auth.signup.back")}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {translations("auth.signup.creating")}
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              {translations("auth.signup.complete")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AddressForm;